import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useLazyGetCoCustomerTimeSeriesQuery } from 'src/services';
import { useAppSelector } from 'src/store';
import { TotalProcessedBarChart } from 'src/pages/AnalyticsPage/AnalyticsCustomer/TotalProcessedBarChart/TotalProcessedBarChart';
import { API_VERSION } from 'src/constants/api-routes';
import { isErrorStatus } from 'src/utils/get-error-message';
import type { WeekRange } from 'src/pages/ControlDeskPage/WeeklyAnalyticsSection/WeeklyDateSelect';
import { useLoaderWhile } from 'src/hooks/useLoaderWhile';
import { getApiVersion } from 'src/utils/apiVersion';

export const WeeklyAnalyticsChart = ({
  dataKey = '',
  dateRange,
  dataKeyStack = '',
  hideLabel = false,
}: {
  dataKey?: string;
  dateRange?: WeekRange | null;
  dataKeyStack?: string;
  hideLabel?: boolean;
}) => {
  const { id } = useParams();
  const version = getApiVersion();
  const isV1 = version === API_VERSION.V1;
  const { timezone } = useAppSelector((s) => s.analyticSlices);
  const [timeSeriesRequest, setTimeSeriesRequest] = useState({
    project_ids: [id],
    start_time: dateRange?.start_time,
    end_time: dateRange?.end_time,
    group_by: 'day',
    tz_offset: timezone.offset * 60,
  });
  const [timeSeriesResponse, setTimeSeriesResponse] = useState([]);
  const [fetchTimeSeriesQuery, { data: timeSeriesData, isFetching, isError, error }] =
    useLazyGetCoCustomerTimeSeriesQuery();

  useLoaderWhile(isFetching);

  useEffect(() => {
    if (isError) {
      isErrorStatus(error, 404) && setTimeSeriesResponse([]);
    }
  }, [isError]);

  useEffect(() => {
    if (!dateRange) {
      return;
    }
    setTimeSeriesRequest({
      ...timeSeriesRequest,
      project_ids: [id],
      start_time: dateRange?.start_time,
      end_time: dateRange?.end_time,
      group_by: 'day',
      tz_offset: timezone.offset * 60,
    });
  }, [id, dateRange?.start_time, dateRange?.start_time, timezone]);

  useEffect(() => {
    if (isV1 && timeSeriesRequest?.end_time && timeSeriesRequest.start_time) {
      fetchTimeSeriesQuery(timeSeriesRequest);
    }
  }, [timeSeriesRequest]);

  useEffect(() => {
    if (timeSeriesData?.length) {
      setTimeSeriesResponse(timeSeriesData);
    } else {
      setTimeSeriesResponse([]);
    }
  }, [timeSeriesData]);

  return (
    <div className="w-full">
      {timeSeriesResponse.length ? (
        <div className={'w-full h-full'}>
          <TotalProcessedBarChart
            hideLabel={hideLabel}
            data={timeSeriesResponse}
            dataKey={dataKey}
            dataKeyStack={dataKeyStack}
            groupBy={timeSeriesRequest.group_by}
            minHeight="260px"
            height={260}
          />
        </div>
      ) : (
        <div
          className={
            'w-full h-[260px] flex items-center justify-center font-medium text-[#7A6BE5] text-xs'
          }
        >
          No data available for this week.
        </div>
      )}
    </div>
  );
};
